import { useCallback, useState } from 'react';

import { Accordion } from '@zillow/constellation-10';

import { getPersistedExpandedGroups, persistExpandedGroups } from '../../../utils/flexiblePropertiesPageUtils';

import PropertiesConstants from '../../../constants/PropertiesConstants';

import { accordionItemStyles, accordionRootStyles } from '../styles';

import FlexiblePropertyCard from './FlexiblePropertyCard';

import type { AccountViewListingGroupDto } from '@zg-rentals/ts-rental-manager-api-client';

type Props = {
  listingGroups: Array<AccountViewListingGroupDto>;
  isLoading?: boolean;
};

const createSkeletonListing = (index: number): AccountViewListingGroupDto =>
  ({
    groupId: `skeleton-${index}`,
    listingAlias: `skeleton-${index}`,
    name: '',
    photoKey: '',
    address: { street: '', city: '', state: '', zip: '', unit: '' },
  }) as AccountViewListingGroupDto;

const toGroupIdList = (value: string | Array<string>): Array<string> =>
  Array.isArray(value) ? value : value ? [value] : [];

export const FlexiblePropertyCardList = ({ listingGroups, isLoading = false }: Props) => {
  const showSkeletons = isLoading && listingGroups.length === 0;

  const [expandedGroupIds, setExpandedGroupIds] = useState<Array<string>>(getPersistedExpandedGroups);

  const handleExpandedChange = useCallback((value: string | Array<string>) => {
    const groupIds = toGroupIdList(value);
    setExpandedGroupIds(groupIds);
    persistExpandedGroups(groupIds);
  }, []);

  const items = showSkeletons
    ? Array.from({ length: PropertiesConstants.PROPERTIES_LIMIT_DEFAULT }, (_, index) => createSkeletonListing(index))
    : listingGroups;

  return (
    <Accordion.Root
      data-testid="FlexiblePropertyCardList"
      aria-label="Properties"
      expanded={expandedGroupIds}
      onExpandedChange={handleExpandedChange}
      css={accordionRootStyles}
    >
      {items.map((listing) => (
        <Accordion.Item key={listing.groupId} value={listing.groupId} css={accordionItemStyles}>
          <FlexiblePropertyCard
            listing={listing}
            isExpanded={expandedGroupIds.includes(listing.groupId)}
            isLoading={showSkeletons}
          />
        </Accordion.Item>
      ))}
    </Accordion.Root>
  );
};
